import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { updateStudent } from "../../../store/studentsSlice";
import { getUserById } from "../../../api/usersService";
import useDistricts from "../../../hooks/useDistricts";
import { activePolygonNames } from "../../../utils/districts";

/**
 * Poligonul elevului (ADMIN/MANAGER). Contează doar când sunt mai multe
 * poligoane: elevul alege o dată la prima programare, personalul îl poate
 * schimba aici; gol = elevul alege din nou la următoarea programare.
 * Se salvează imediat, separat de restul formularului.
 *
 * Doar logica (hook): câmpul îl desenează fila Profil cu InputUI as="select", ca să stea în
 * același InputUI.Group (colțurile lipite) cu restul câmpurilor.
 * Întoarce null când nu e nimic de ales (un singur poligon sau niciun elev).
 */
export function useStudentPolygon({ studentId, value, onSaved }) {
   const dispatch = useDispatch();
   const districts = useDistricts();
   const polygons = activePolygonNames(districts);
   const [current, setCurrent] = useState(value || "");
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState("");

   const multiple = polygons.length >= 2;

   useEffect(() => {
      setCurrent(value || "");
   }, [value]);

   // lista elevilor nu aduce mereu câmpul: îl citim din profilul elevului
   useEffect(() => {
      if (!multiple || !studentId) return undefined;
      let alive = true;
      getUserById(studentId)
         .then((user) => {
            if (alive) setCurrent(user?.polygonDistrict || "");
         })
         .catch(() => {});
      return () => {
         alive = false;
      };
   }, [multiple, studentId]);


   const save = async (next) => {
      const previous = current;
      setCurrent(next);
      setBusy(true);
      setError("");
      try {
         const updated = await dispatch(
            updateStudent({ id: studentId, data: { polygonDistrict: next || null } }),
         ).unwrap();
         const saved = updated?.polygonDistrict ?? (next || null);
         setCurrent(saved || "");
         if (typeof onSaved === "function") onSaved(saved);
      } catch (err) {
         setCurrent(previous);
         setError(err?.message || "Nu am putut salva poligonul.");
      } finally {
         setBusy(false);
      }
   };

   if (!studentId || !multiple) return null;

   const options = [
      { value: "", label: "Alege la programare" },
      ...polygons.map((name) => ({ value: name, label: name })),
      ...(current && !polygons.includes(current) ? [{ value: current, label: `${current} (inactiv)` }] : []),
   ];
   return { current, busy, error, options, save };
}
